import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  Plus, 
  Copy, 
  Trash2, 
  Edit, 
  ToggleLeft, 
  ToggleRight,
  Download,
  RefreshCw,
  Users,
  Calendar,
  TrendingUp,
  Eye,
  X,
  Ticket
} from 'lucide-react';
import { AdminLayout, AdminStatCard } from '../../components/admin/layout';

function PromoCodeAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedCodeUsers, setSelectedCodeUsers] = useState([]);
  const [selectedCodeInfo, setSelectedCodeInfo] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // 새 프로모션 코드 생성 폼 상태
  const [newPromoForm, setNewPromoForm] = useState({
    prefix: '',
    isShared: true,
    count: 10,
    type: 'promotion',
    maxUses: '100',
    maxUsesPerUser: 1,
    validDays: '',
    benefitDurationDays: '180',
    description: ''
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role === 'admin') {
        setIsAdmin(true);
        await loadPromoCodesAndStats();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadPromoCodesAndStats = async () => {
    try {
      setRefreshing(true);
      
      // 프로모션 코드 목록 로드
      const { data: codesData, error: codesError } = await supabase
        .rpc('get_promo_codes_admin');
      
      if (codesError) {
        console.error('프로모션 코드 로드 에러:', codesError);
        if (codesError.message?.includes('Admin access required')) {
          alert('관리자 권한이 필요합니다.');
          navigate('/');
          return;
        }
        throw codesError;
      }
      setPromoCodes(codesData || []);

      // 통계 로드
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_promo_code_stats');
      
      if (statsError) {
        console.error('통계 로드 에러:', statsError);
        setStats({});
      } else {
        setStats(statsData || {});
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert(`데이터 로드 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreatePromoCodes = async () => {
    try {
      const params = {
        p_prefix: newPromoForm.prefix.toUpperCase(),
        p_count: newPromoForm.isShared ? 1 : parseInt(newPromoForm.count),
        p_type: newPromoForm.type,
        p_benefit_duration_days: parseInt(newPromoForm.benefitDurationDays),
        p_max_uses: newPromoForm.isShared ? (newPromoForm.maxUses ? parseInt(newPromoForm.maxUses) : null) : 1,
        p_valid_days: newPromoForm.validDays ? parseInt(newPromoForm.validDays) : null
      };
      
      console.log('프로모션 코드 생성 파라미터:', params);

      const { data, error } = await supabase.rpc('generate_promo_codes', params);

      if (error) {
        console.error('프로모션 코드 생성 오류:', error);
        throw error;
      }

      alert(`${data.length}개의 프로모션 코드가 생성되었습니다!`);
      setShowCreateModal(false);
      await loadPromoCodesAndStats();
      
      // 폼 초기화
      setNewPromoForm({
        prefix: '',
        isShared: true,
        count: 10,
        type: 'promotion',
        maxUses: '100',
        maxUsesPerUser: 1,
        validDays: '',
        benefitDurationDays: '180',
        description: ''
      });
    } catch (error) {
      console.error('Error creating promo codes:', error);
      alert(`프로모션 코드 생성 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    }
  };

  const togglePromoCodeStatus = async (codeId) => {
    try {
      const { data, error } = await supabase.rpc('toggle_promo_code_status', {
        p_code_id: codeId
      });

      if (error) throw error;
      await loadPromoCodesAndStats();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('정말 이 프로모션 코드를 삭제하시겠습니까?')) return;

    try {
      const { data, error } = await supabase.rpc('delete_promo_code', {
        p_code_id: codeId
      });

      if (error) {
        console.error('삭제 에러:', error);
        throw error;
      }
      
      if (data && data.success) {
        alert(data.message);
        await loadPromoCodesAndStats();
      } else {
        alert(data?.message || '삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Error deleting promo code:', error);
      alert(error.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사되었습니다!');
  };

  const exportPromoCodes = () => {
    const csv = [
      ['코드', '타입', '설명', '혜택 기간(일)', '최대 사용 횟수', '사용된 횟수', '상태', '만료일'],
      ...promoCodes.map(code => [
        code.code,
        code.type,
        code.description || '',
        code.benefit_duration_days || '0',
        code.max_uses || '무제한',
        code.used_count,
        code.is_active ? '활성' : '비활성',
        code.valid_until ? new Date(code.valid_until).toLocaleDateString() : '무기한'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo_codes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const viewCodeUsers = async (code) => {
    setSelectedCodeInfo(code);
    setShowUsersModal(true);
    setLoadingUsers(true);
    
    try {
      const { data, error } = await supabase.rpc('get_promo_code_users', {
        p_code_id: code.id
      });
      
      if (error) throw error;
      setSelectedCodeUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('사용자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const actionButtons = (
    <>
      <button
        onClick={() => setShowCreateModal(true)}
        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
      >
        <Plus className="h-5 w-5" />
        새 프로모션 코드 생성
      </button>
      
      <button
        onClick={loadPromoCodesAndStats}
        disabled={refreshing}
        className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:bg-gray-400"
      >
        <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        새로고침
      </button>

      <button
        onClick={exportPromoCodes}
        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
      >
        <Download className="h-5 w-5" />
        CSV 내보내기
      </button>
    </>
  );

  return (
    <AdminLayout
      title="프로모션 코드 관리"
      description="프로모션 코드를 생성하고 관리합니다."
      icon={<Ticket className="h-8 w-8 text-blue-600" />}
      actions={actionButtons}
      isAdmin={isAdmin}
      loading={loading}
    >
      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <AdminStatCard
            icon={<TrendingUp />}
            title="전체 코드"
            value={stats.total_codes || 0}
            iconColorClass="text-blue-600"
          />
          
          <AdminStatCard
            icon={<ToggleRight />}
            title="활성 코드"
            value={stats.active_codes || 0}
            iconColorClass="text-green-600"
          />
          
          <AdminStatCard
            icon={<Calendar />}
            title="총 사용 횟수"
            value={stats.total_uses || 0}
            iconColorClass="text-purple-600"
          />
          
          <AdminStatCard
            icon={<Users />}
            title="활성 사용자"
            value={stats.active_users || 0}
            iconColorClass="text-indigo-600"
          />
        </div>
      )}

      {/* 프로모션 코드 테이블 */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  코드
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  타입
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  프로모션 혜택 기간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용 현황
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  프로모션 코드 유효기간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promoCodes.map((code) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {code.code}
                      </code>
                      <button
                        onClick={() => copyToClipboard(code.code)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="코드 복사"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      code.type === 'early_access' ? 'bg-green-100 text-green-800' :
                      code.type === 'promotion' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {code.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {code.benefit_duration_days || 0}일
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {code.used_count} / {code.max_uses || '∞'}
                    </div>
                    {code.usage_rate !== null && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(code.usage_rate, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {code.valid_until ? (
                        <span className={new Date(code.valid_until) < new Date() ? 'text-red-600' : ''}>
                          {new Date(code.valid_until).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-gray-500">무기한</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => togglePromoCodeStatus(code.id)}
                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${
                        code.is_active 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {code.is_active ? (
                        <>
                          <ToggleRight className="h-4 w-4" />
                          활성
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-4 w-4" />
                          비활성
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => viewCodeUsers(code)}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-blue-600 bg-white rounded-md hover:bg-blue-50 transition-colors text-xs"
                      title="사용자 목록 보기"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      사용자 보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 최근 사용 내역 */}
      {stats?.recent_uses && stats.recent_uses.length > 0 && (
        <div className="mt-8 bg-white shadow-lg rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">최근 사용 내역</h3>
          <div className="space-y-2">
            {stats.recent_uses.map((use, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                <div>
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {use.code}
                  </span>
                  <span className="ml-3 text-sm text-gray-600">
                    {use.user_email}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {new Date(use.used_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 프로모션 코드 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">새 프로모션 코드 생성</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    코드 이름/접두사
                  </label>
                  <input
                    type="text"
                    value={newPromoForm.prefix}
                    onChange={(e) => setNewPromoForm({...newPromoForm, prefix: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={newPromoForm.isShared ? "예: KAIST-BETA" : "예: BETA"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    생성 방식
                  </label>
                  <select
                    value={newPromoForm.isShared ? 'shared' : 'individual'}
                    onChange={(e) => {
                      const isShared = e.target.value === 'shared';
                      setNewPromoForm({
                        ...newPromoForm, 
                        isShared,
                        maxUses: isShared ? '100' : '1',
                        count: isShared ? 1 : 10
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="shared">공유 코드 (하나의 코드를 여러 명이 사용)</option>
                    <option value="individual">개별 코드 (1인 1코드)</option>
                  </select>
                </div>
              </div>

              {!newPromoForm.isShared && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    생성할 코드 개수
                  </label>
                  <input
                    type="number"
                    value={newPromoForm.count}
                    onChange={(e) => setNewPromoForm({...newPromoForm, count: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="1000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newPromoForm.prefix}-XXXX-XXXX 형식으로 {newPromoForm.count}개가 생성됩니다
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  타입
                </label>
                <select
                value={newPromoForm.type}
                onChange={(e) => {
                  const type = e.target.value;
                  setNewPromoForm({
                    ...newPromoForm, 
                    type,
                    benefitDurationDays: type === 'early_access' ? '730' : '180'
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                <option value="promotion">프로모션 (기본 6개월)</option>
                <option value="early_access">얼리 액세스 (기본 2년)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    최대 사용 횟수
                  </label>
                  {newPromoForm.isShared ? (
                    <input
                      type="number"
                      value={newPromoForm.maxUses}
                      onChange={(e) => setNewPromoForm({...newPromoForm, maxUses: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="비워두면 무제한"
                    />
                  ) : (
                    <input
                      type="number"
                      value="1"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {newPromoForm.isShared 
                      ? '공유 코드는 여러 명이 하나의 코드를 사용합니다' 
                      : '개별 코드는 자동으로 1회로 제한됩니다'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    코드 유효 일수
                  </label>
                  <input
                    type="number"
                    value={newPromoForm.validDays}
                    onChange={(e) => setNewPromoForm({...newPromoForm, validDays: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="비워두면 무기한"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    코드를 사용할 수 있는 기간
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  혜택 제공 일수
                </label>
                <input
                  type="number"
                  value={newPromoForm.benefitDurationDays}
                  onChange={(e) => setNewPromoForm({...newPromoForm, benefitDurationDays: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 180"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  사용자가 코드를 사용한 후 유료 기능을 사용할 수 있는 일수
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleCreatePromoCodes}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                생성하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 목록 모달 */}
      {showUsersModal && selectedCodeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold">프로모션 코드 사용자</h2>
                <p className="text-sm text-gray-600 mt-1">
                  코드: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{selectedCodeInfo.code}</span>
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setSelectedCodeUsers([]);
                  setSelectedCodeInfo(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {loadingUsers ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">사용자 목록을 불러오는 중...</p>
                </div>
              </div>
            ) : selectedCodeUsers.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray-500">아직 이 코드를 사용한 사용자가 없습니다.</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        사용자
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        사용일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        만료일
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        포트폴리오 수
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        상태
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedCodeUsers.map((user) => (
                      <tr key={user.user_id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || '이름 없음'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(user.used_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.expires_at ? new Date(user.expires_at).toLocaleDateString() : '무기한'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.portfolio_count}개
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_active ? '활성' : '비활성'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default PromoCodeAdmin;
